import { adminRequest, hashFile, operationKey } from "./adminSiteApi";
export const BASE = "/admin/paperwork";
export const STATES =
  "AL AZ AR CA CO CT DE FL GA ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(
    " ",
  );
export const EQUIPMENT = {
  dry_van: "Dry Van",
  box_truck: "Box Truck",
  reefer: "Reefer",
};
export const KINDS = [
  "terms",
  "license",
  "refund",
  "tax",
  "updates",
  "downloads",
];
export const PART_BYTES = 4 * 1024 * 1024;
export const MAX_BYTES = 50 * 1024 * 1024;
export const pathId = (id) => encodeURIComponent(id);
export const productPath = (id) => `${BASE}/products/${pathId(id)}`;
export function minorUnits(value) {
  if (value === "") return null;
  if (!/^(0|[1-9][0-9]{0,6})(\.[0-9]{1,2})?$/.test(value))
    throw Error("Enter a positive USD price with at most two decimal places.");
  const [whole, cents = ""] = value.split(".");
  const amount = Number(whole) * 100 + Number(cents.padEnd(2, "0"));
  if (amount < 1 || amount > 100000000)
    throw Error("USD price must be between 0.01 and 1,000,000.00.");
  return amount;
}
export const dollars = (minor) =>
  minor == null
    ? ""
    : `${Math.floor(minor / 100)}.${String(minor % 100).padStart(2, "0")}`;
export const money = (minor) =>
  minor == null ? "Not set" : `USD ${dollars(minor)}`;
export async function request(path, options) {
  try {
    return (await adminRequest(path, options)).data;
  } catch (e) {
    if (e.code === "file_not_ready")
      e.message =
        "This ZIP needs successful structural checks and owner review.";
    if (e.code === "catalogue_changed")
      e.message =
        "Catalogue changed. Review a new import preview before applying mappings.";
    throw e;
  }
}
export function fileState(validation) {
  if (!validation) return "Awaiting checks";
  if (validation.state === "rejected" || validation.state === "withdrawn")
    return "Rejected";
  if (validation.state === "ready") return "Ready for publication";
  if (validation.checksumVerified && validation.archiveVerified)
    return "Awaiting owner review";
  return "Awaiting checks";
}
export function cellState(product, mapping, file) {
  if (!mapping) return "Missing";
  if (mapping.status === "unlisted") return "Unlisted";
  if (!mapping.fileVersionId) return "Draft";
  const state = fileState(file?.validation);
  if (state !== "Ready for publication") return state;
  if (mapping.status === "draft") return "Draft";
  if (product.status === "unlisted") return "Unlisted";
  return product.status === "published" &&
    product.publishedRevision === product.revision
    ? "Published"
    : "Ready for publication";
}
export function uploadState(upload, checking = false) {
  if (checking) return "Checking";
  if (upload.status !== "complete" && upload.expiresAt <= Date.now())
    return "Expired";
  if (upload.status === "assembling") return "Assembling";
  if (upload.status !== "complete") return "Uploading";
  const state = fileState(upload.validation);
  return state === "Ready for publication"
    ? "Ready"
    : state === "Awaiting checks"
      ? "Queued for checks"
      : state;
}
export function revisionSet(record) {
  return {
    expectedRevision: record.product.revision,
    expectedCoverageRevision: record.product.coverage.revision,
    variantRevisions: record.variants.map(({ id, revision }) => ({
      id,
      revision,
    })),
  };
}
export function publicationBlockers(record, policies) {
  const { product: p, variants, versions } = record,
    reasons = [];
  const enabled = variants.filter((v) => v.status === "enabled");
  if (!enabled.length) reasons.push("Enable at least one reviewed mapping.");
  if (
    p.coverage.mode === "complete" &&
    enabled.length !== p.coverage.states.length * p.coverage.equipment.length
  )
    reasons.push(
      "Complete coverage requires every selected cell to be enabled.",
    );
  for (const v of enabled) {
    const file = versions.find((f) => f.ref.id === v.fileVersionId);
    if (
      !v.mappingReviewedBy ||
      !file ||
      file.validation.state !== "ready" ||
      !file.validation.checksumVerified ||
      !file.validation.archiveVerified ||
      !file.validation.reviewedBy
    )
      reasons.push(
        `${v.registrationState} / ${EQUIPMENT[v.equipment]} needs file and mapping review.`,
      );
    if ((v.priceMinor ?? p.priceMinor) == null)
      reasons.push(
        `${v.registrationState} / ${EQUIPMENT[v.equipment]} needs a price.`,
      );
  }
  for (const kind of KINDS)
    if (
      !p.policies?.[kind] ||
      policies[kind]?.id !== p.policies[kind] ||
      policies[kind]?.kind !== kind ||
      policies[kind]?.state !== "reviewed"
    )
      reasons.push(
        `Load the reviewed ${kind} policy matching the saved product.`,
      );
  return reasons;
}
export async function matchFiles(files, manifest, onProgress = () => {}) {
  if (
    !manifest ||
    manifest.schemaVersion !== 1 ||
    !Array.isArray(manifest.files) ||
    !Array.isArray(manifest.mappings)
  )
    throw Error("Choose a schemaVersion 1 manifest with files and mappings.");
  if (files.length !== manifest.files.length || !files.length)
    throw Error("Reselect every ZIP named in this manifest.");
  const selected = new Map(),
    aliases = new Set();
  for (const f of manifest.files) {
    if (aliases.has(f.clientFileId)) throw Error("Duplicate manifest alias.");
    aliases.add(f.clientFileId);
    const matches = files.filter((x) => x.name === f.fileName);
    if (
      matches.length !== 1 ||
      selected.has(matches[0]) ||
      matches[0].size !== f.size ||
      f.size > MAX_BYTES ||
      f.size < 1
    )
      throw Error(
        `Missing, ambiguous or incorrect-size file: ${f.clientFileId}`,
      );
    const file = matches[0];
    const checksum = await hashFile(file, (progress) =>
      onProgress(`${f.clientFileId}: hashing ${Math.round(progress * 100)}%`),
    );
    if (checksum !== f.checksum)
      throw Error(`Checksum mismatch: ${f.clientFileId}`);
    selected.set(file, f.clientFileId);
  }
  return Object.fromEntries([...selected].map(([f, alias]) => [alias, f]));
}
// Only opaque identifiers, checksums, revision numbers and operation keys survive reload.
// Files, Firebase credentials, policies and storage URLs never enter this journal.
export function createJournal(storage, scope) {
  const name = `magellan-paperwork-v1:${scope}`;
  let value;
  try {
    value = JSON.parse(storage.getItem(name) || "null");
  } catch {
    /* damaged local state is not authority */
  }
  if (
    !value ||
    !Array.isArray(value.uploads) ||
    !Array.isArray(value.imports) ||
    !value.operations
  )
    value = { uploads: [], imports: [], operations: {} };
  const save = () => storage.setItem(name, JSON.stringify(value));
  return {
    value,
    remember(kind, item) {
      value[kind] = [...value[kind].filter((x) => x.id !== item.id), item];
      save();
    },
    key(tag, revision) {
      if (!value.operations[tag]) {
        value.operations[tag] = {
          key: operationKey(),
          ...(revision == null ? {} : { revision }),
        };
        save();
      }
      return value.operations[tag];
    },
    clear(tag) {
      delete value.operations[tag];
      save();
    },
  };
}
export async function transfer({
  upload,
  file,
  journal,
  send = request,
  paused = () => false,
  progress = () => {},
}) {
  const url = `${BASE}/uploads/${pathId(upload.uploadId)}`;
  let current = await send(url);
  for (
    let part = 0;
    part * PART_BYTES < file.size && current.status === "uploading";
    part++
  ) {
    if (paused()) return current;
    if (current.parts.some((p) => p.part === part)) continue;
    const chunk = file.slice(part * PART_BYTES, (part + 1) * PART_BYTES);
    const checksum = await hashFile(chunk);
    if (paused()) return current;
    const tag = `part:${current.uploadId}:${part}`;
    await send(`${url}/parts/${part}`, {
      method: "PUT",
      binary: true,
      body: chunk,
      partChecksum: checksum,
      key: journal.key(tag).key,
    });
    journal.clear(tag);
    current = await send(url);
    progress(current);
  }
  if (paused() || current.status === "complete") return current;
  if (current.leaseUntil > Date.now())
    throw Error("Assembly is in progress. Refresh status before retrying.");
  const tag = `complete:${current.uploadId}`;
  // An expired assembly lease invalidates its old claim; acquire against fresh GET state.
  if (current.status === "assembling" && current.leaseUntil <= Date.now())
    journal.clear(tag);
  const pending = journal.key(tag, current.revision);
  const result = await send(`${url}/complete`, {
    method: "POST",
    key: pending.key,
    body: { expectedRevision: pending.revision },
  });
  journal.clear(tag);
  progress(result);
  return result;
}
